/**
 * ScopeSync — keeps the URL and the session scope store in agreement.
 *
 * Routes carry the scope (/o/:orgId, /o/:orgId/s/:siteId/...) so links are
 * deep-linkable and server-side-RBAC-checkable (decision #9). This component
 * reads those params and pushes them into the Zustand store so every
 * scope-prefixed query key and the ScopeBar reflect the URL — including on a
 * cold deep-link load. Renders its children via <Outlet/>.
 */
import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useSession } from "@/stores/session";

export function ScopeSync() {
  const { orgId, siteId } = useParams();
  const me = useSession((s) => s.me);
  const currentOrgId = useSession((s) => s.currentOrgId);
  const currentSiteId = useSession((s) => s.currentSiteId);
  const setOrg = useSession((s) => s.setOrg);
  const setSite = useSession((s) => s.setSite);

  // Sync org from URL. Only apply ids the user actually has (defense in depth;
  // the server is still the authority).
  useEffect(() => {
    if (!me || !orgId) return;
    const valid = me.orgs.some((o) => o.id === orgId);
    if (valid && orgId !== currentOrgId) setOrg(orgId);
  }, [me, orgId, currentOrgId, setOrg]);

  // Sync site from URL (may be absent => org-wide).
  useEffect(() => {
    if (!me) return;
    const nextSite = siteId ?? null;
    if (nextSite && !me.sites.some((s) => s.id === nextSite)) return;
    if (nextSite !== currentSiteId) setSite(nextSite);
  }, [me, siteId, currentSiteId, setSite]);

  return <Outlet />;
}
